import {
  agruparPorSecao,
  cabecalhoAssinatura,
  uuidv7,
  type CampoChecklist,
  type Foto,
  validarInspecao,
  type Problema,
  type Resposta,
  type ValorResposta,
  valorDaResposta,
} from '@qsms/shared'
import { useLiveQuery } from 'dexie-react-hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CampoResposta, NAO_SE_APLICA } from '../componentes/CampoResposta.js'
import { ExcluirInspecao } from '../componentes/ExcluirInspecao.js'
import { GaleriaFotos } from '../componentes/GaleriaFotos.js'
import { sessaoAtual } from '../dados/api.js'
import { banco, CHAVES, lerAjuste, type InspecaoLocal, type ResponsavelNc } from '../dados/banco.js'
import { antesDeRecarregar } from '../dados/recarregar.js'
import { enfileirar, registrarNaFila, sincronizar } from '../dados/sincronizacao.js'

/// Preenchimento da inspeção. Serve tanto para o formulário INI quanto para os 141
/// checklists dinâmicos — a diferença está nos dados, não no código.
///
/// **Nada aqui espera a rede.** Cada alteração é gravada no IndexedDB depois de uma
/// pausa curta de digitação; "Finalizar" valida e põe na fila. O inspetor pode
/// fechar o app, ficar sem bateria ou passar o dia sem sinal sem perder nada.

export function Preencher() {
  const { id } = useParams<{ id: string }>()
  const navegar = useNavigate()
  const [problemas, setProblemas] = useState<Problema[]>([])
  const [salvandoEm, setSalvandoEm] = useState<string | null>(null)
  const [erroGravacao, setErroGravacao] = useState<string | null>(null)

  // `null` quando não existe: o Dexie devolve undefined, que aqui quer dizer
  // "carregando". Sem a troca, uma inspeção excluída com a tela aberta ficava em
  // "Carregando…" para sempre.
  const inspecao = useLiveQuery(
    async (): Promise<InspecaoLocal | null> => (id ? ((await banco.inspecoes.get(id)) ?? null) : null),
    [id],
  )
  // `null` quando o checklist não está no aparelho — desativado por um
  // administrador depois que a inspeção começou. `undefined` é "carregando".
  const checklist = useLiveQuery(
    async () => (inspecao ? ((await banco.checklists.get(inspecao.checklistId)) ?? null) : undefined),
    [inspecao?.checklistId],
  )
  const campos = useLiveQuery(
    async () =>
      inspecao
        ? (await banco.campos.where('checklistId').equals(inspecao.checklistId).toArray()).sort(
            (a, b) => a.ordem - b.ordem,
          )
        : [],
    [inspecao?.checklistId],
    [],
  )

  const valores = useMemo(() => {
    const mapa = new Map<string, ValorResposta>()
    for (const r of inspecao?.respostas ?? []) mapa.set(r.campoChecklistId, valorDaResposta(r))
    return mapa
  }, [inspecao?.respostas])

  // Agrupa uma vez: 141 checklists e alguns têm mais de 100 campos.
  const secoes = useMemo(() => agruparPorSecao(campos), [campos])

  // Campos de escolher um nome em vez de digitar (Gerente Contrato). Lista vazia —
  // aparelho que ainda não sincronizou depois da mudança — continua como texto:
  // uma lista sem nomes num campo obrigatório impediria finalizar sem rede.
  const listas = useLiveQuery(
    () => lerAjuste<Record<string, string[]>>(CHAVES.listasDePessoas, {}),
    [],
    {} as Record<string, string[]>,
  )
  // Os responsáveis por NC da obra desta inspeção. A lista do aparelho é
  // relida a cada gravação de ajuste; guardar a anterior quando nada mudou evita
  // redesenhar os cem campos do formulário a cada sincronização.
  // `null` enquanto a lista não chegou ao aparelho (servidor antigo, primeira
  // sincronização ainda não feita): a tela avisa, em vez de dizer que a obra não
  // tem ninguém.
  const todosResponsaveis = useLiveQuery(
    () => lerAjuste<ResponsavelNc[] | null>(CHAVES.responsaveisNc, null),
    [],
    null,
  )
  const responsaveisAnteriores = useRef<OpcaoResponsavel[]>([])
  const obraId = inspecao?.obraId
  const responsaveisDaObra = useMemo(() => {
    if (todosResponsaveis === null) return null
    const nova = obraId
      ? todosResponsaveis.filter((r) => r.obraIds.includes(obraId)).map((r) => ({ id: r.id, nome: r.nome }))
      : []
    const antes = responsaveisAnteriores.current
    const igual = nova.length === antes.length && nova.every((r, i) => r.id === antes[i]!.id && r.nome === antes[i]!.nome)
    if (!igual) responsaveisAnteriores.current = nova
    return responsaveisAnteriores.current
  }, [todosResponsaveis, obraId])

  const codigoObra = inspecao?.codigo.split('-')[0] ?? null
  const listaPorCampo = useMemo(() => {
    const mapa = new Map<string, readonly string[]>()
    for (const campo of campos) {
      const nomes = listas[campo.nome]
      if (campo.tipoCampo !== 'TEXTO' || !nomes || nomes.length === 0) continue
      mapa.set(
        campo.id,
        campo.nome === 'Gerente Contrato' && codigoObra === '0001' ? [NAO_SE_APLICA, ...nomes] : nomes,
      )
    }
    return mapa
  }, [campos, listas, codigoObra])

  // O que foi mexido e ainda não chegou ao banco.
  const pendentes = useRef<Map<string, ValorResposta>>(new Map())
  const fotosPendentes = useRef<Array<(fotos: Foto[]) => Foto[]>>([])
  // Imagens escolhidas e trocadas antes de chegar a ser gravadas: não são de
  // ninguém, e saem junto com a próxima gravação.
  const orfaos = useRef<string[]>([])
  const temporizador = useRef<number | null>(null)
  const camposRef = useRef(campos)
  camposRef.current = campos

  /// Grava o pendente sobre o registro **atual** do banco, lido dentro da transação.
  ///
  /// Antes partia da inspeção que a tela tinha desenhado, e duas gravações seguidas
  /// — a pausa na digitação, a saída da tela, uma escrita da sincronização no meio —
  /// podiam partir da mesma base: a segunda apagava a resposta que a primeira tinha
  /// acabado de gravar. E, dependendo da inspeção que muda a cada gravação, a função
  /// mudava de identidade e fazia o efeito de saída disparar a cada tecla gravada.
  const gravar = useCallback(async (): Promise<void> => {
    if (!id) return
    if (temporizador.current !== null) {
      window.clearTimeout(temporizador.current)
      temporizador.current = null
    }
    const alteracoes = new Map(pendentes.current)
    const mudancasFotos = fotosPendentes.current.splice(0)
    const semDono = orfaos.current.splice(0)
    pendentes.current.clear()
    if (alteracoes.size === 0 && mudancasFotos.length === 0) return

    const camposPorId = new Map(camposRef.current.map((c) => [c.id, c]))
    const soltos: string[] = []
    let gravou = false
    try {
      gravou = await banco.transaction('rw', banco.inspecoes, banco.fila, async () => {
        const atual = await banco.inspecoes.get(id)
        if (!atual) return false

        const respostas = new Map(atual.respostas.map((r) => [r.campoChecklistId, r]))
        for (const [campoId, valor] of alteracoes) {
          const campo = camposPorId.get(campoId)
          if (!campo) continue
          const anterior = respostas.get(campoId)
          if (anterior?.arquivoId && anterior.arquivoId !== valor.arquivoId) {
            soltos.push(anterior.arquivoId)
          }
          respostas.set(campoId, {
            id: anterior?.id ?? uuidv7(),
            campoChecklistId: campoId,
            campoNome: campo.nome,
            campoTipo: campo.tipoCampo,
            ordem: campo.ordem,
            ...valor,
            opcoes: valor.opcoes ?? [],
          } satisfies Resposta)
        }

        let fotos = atual.fotos
        for (const mudar of mudancasFotos) fotos = mudar(fotos)
        const ficaram = new Set(fotos.map((f) => f.arquivoId))
        for (const f of atual.fotos) if (!ficaram.has(f.arquivoId)) soltos.push(f.arquivoId)

        // Bloqueada (conflito, recusa do servidor): grava, mas não põe na fila nem
        // apaga o aviso. Reenviar a cada pausa bateria no mesmo conflito, e o aviso
        // apareceria e sumiria empurrando o formulário. A nova tentativa fica com
        // "Salvar rascunho" e "Finalizar".
        const bloqueada = atual.sincronizacao === 'BLOQUEADO'
        const lista = [...respostas.values()]
        // Uma finalizada que a pessoa reabriu e deixou incompleta — uma NC nova
        // ainda sem responsável, um obrigatório apagado — volta a "em andamento".
        // Subindo como finalizada, o servidor a recusaria a cada pausa de
        // digitação. Finalizar de novo valida, como na primeira vez.
        let status = atual.status === 'RASCUNHO' ? 'EM_ANDAMENTO' : atual.status
        if (status === 'FINALIZADO' && camposPorId.size > 0) {
          const mapa = new Map(lista.map((r) => [r.campoChecklistId, valorDaResposta(r)]))
          if (validarInspecao(camposRef.current, mapa).length > 0) status = 'EM_ANDAMENTO'
        }
        await banco.inspecoes.update(id, {
          respostas: lista,
          fotos,
          atualizadoEm: new Date().toISOString(),
          status,
          // Mexer numa inspeção já enviada a torna pendente de novo. Sem isto ela
          // continuava marcada como ENVIADA, o cartão mostrava o tique verde e o
          // próximo pull sobrescrevia a correção com a versão do servidor.
          ...(bloqueada ? {} : { sincronizacao: 'PENDENTE' as const, problema: null }),
        })
        // Na fila, mas sem mandar enviar a cada pausa de digitação.
        if (!bloqueada) await registrarNaFila(id)
        return true
      })
    } catch (e) {
      // A gravação falhou: o que a pessoa fez volta a ficar pendente, sem passar
      // por cima do que ela mexeu depois.
      for (const [campoId, valor] of alteracoes) {
        if (!pendentes.current.has(campoId)) pendentes.current.set(campoId, valor)
      }
      fotosPendentes.current.unshift(...mudancasFotos)
      orfaos.current.push(...semDono)
      throw e
    }

    // Imagem trocada ou removida: o arquivo antigo só sai depois que a resposta nova
    // está gravada. Antes saía primeiro, e fechar o app nesse meio segundo deixava a
    // resposta apontando para uma foto que não existia mais.
    const descartar = [...soltos, ...semDono]
    if (descartar.length > 0) await banco.fotos.bulkDelete(descartar)
    if (gravou) {
      setSalvandoEm(new Date().toISOString())
      setErroGravacao(null)
    }
  }, [id])

  /// Grava agora, e se falhar — armazenamento cheio de fotos, o iOS derrubando o
  /// banco em segundo plano — avisa e tenta de novo. Antes a falha era silenciosa:
  /// o "Salvo às…" ficava velho e a pessoa achava que tinha salvado.
  const gravarComAviso = useCallback(async (): Promise<boolean> => {
    try {
      await gravar()
      return true
    } catch (e) {
      console.error('[preencher] falha ao gravar no aparelho', e)
      setErroGravacao(
        'Não foi possível salvar no aparelho. Se o armazenamento estiver cheio, libere espaço. ' +
          'Tentando de novo…',
      )
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      temporizador.current = window.setTimeout(() => {
        temporizador.current = null
        void gravarComAviso()
      }, 3000)
      return false
    }
  }, [gravar])

  const agendar = useCallback(
    (ms: number) => {
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      temporizador.current = window.setTimeout(() => {
        temporizador.current = null
        void gravarComAviso()
      }, ms)
    },
    [gravarComAviso],
  )

  // Texto grava 600 ms depois da última tecla. Foto e assinatura gravam na hora: são
  // um gesto só, e o arquivo já está no aparelho esperando a resposta apontar para ele.
  const mudarCampo = useCallback(
    (campoId: string, valor: ValorResposta) => {
      const antes = pendentes.current.get(campoId)
      if (antes?.arquivoId && antes.arquivoId !== valor.arquivoId) orfaos.current.push(antes.arquivoId)
      pendentes.current.set(campoId, valor)
      const tipo = camposRef.current.find((c) => c.id === campoId)?.tipoCampo
      agendar(tipo === 'IMAGEM' || tipo === 'ASSINATURA' ? 0 : 600)
    },
    [agendar],
  )

  const mudarFotos = useCallback(
    (mudar: (fotos: Foto[]) => Foto[], imediato: boolean) => {
      fotosPendentes.current.push(mudar)
      agendar(imediato ? 0 : 600)
    },
    [agendar],
  )

  // O botão Atualizar recarrega a página: o que está esperando a pausa de digitação
  // vai para o banco antes.
  useEffect(() => antesDeRecarregar(() => gravar().catch(() => undefined)), [gravar])

  // Grava o que estiver pendente ao mandar o app para segundo plano, e grava e envia
  // ao sair da tela. Com `gravar` estável, isto roda uma vez por inspeção aberta.
  useEffect(() => {
    const aoEsconder = () => {
      if (document.visibilityState === 'hidden') void gravarComAviso()
    }
    document.addEventListener('visibilitychange', aoEsconder)
    return () => {
      document.removeEventListener('visibilitychange', aoEsconder)
      if (temporizador.current !== null) window.clearTimeout(temporizador.current)
      void gravar()
        .catch((e) => console.error('[preencher] falha ao gravar na saída', e))
        .then(() => sincronizar({ aguardarVez: true }))
    }
  }, [gravar, gravarComAviso])

  if (!id) return null
  if (inspecao === undefined) {
    return <p className="py-10 text-center text-slate-500">Carregando…</p>
  }
  if (inspecao === null) {
    return (
      <div className="cartao p-6 text-center">
        <p className="font-semibold text-slate-700">Inspeção não encontrada neste aparelho.</p>
        <button onClick={() => navegar('/')} className="botao-primario mt-4">
          Voltar
        </button>
      </div>
    )
  }

  const somenteLeitura = inspecao.remota || inspecao.status === 'ASSINADO'

  // Sem o checklist não há formulário, e a validação passaria por cima de tudo:
  // finalizar uma inspeção vazia. Ela fica guardada até o checklist voltar.
  const semFormulario = checklist === null

  async function finalizar() {
    if (!inspecao || semFormulario) return
    if (!(await gravarComAviso())) return
    const atual = await banco.inspecoes.get(inspecao.id)
    if (!atual) return

    const mapa = new Map<string, ValorResposta>(atual.respostas.map((r) => [r.campoChecklistId, valorDaResposta(r)]))
    const encontrados = validarInspecao(campos, mapa)
    setProblemas(encontrados)
    if (encontrados.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const sessao = await sessaoAtual()
    await banco.inspecoes.update(inspecao.id, {
      status: 'FINALIZADO',
      titulo: atual.titulo ?? tituloDeduzido(atual.respostas),
      assinaturaUsuario:
        atual.assinaturaUsuario ?? (sessao ? cabecalhoAssinatura(sessao.usuario.nome) : null),
      atualizadoEm: new Date().toISOString(),
    })
    await enfileirar(inspecao.id)
    navegar('/', { replace: true })
  }

  async function salvarRascunho() {
    if (!inspecao) return
    if (!(await gravarComAviso())) return
    await enfileirar(inspecao.id)
    navegar('/', { replace: true })
  }

  return (
    <section>
      <header className="mb-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <button onClick={() => navegar('/')} className="text-sm font-medium text-marinho">
            ← Voltar
          </button>
          {!somenteLeitura && <ExcluirInspecao inspecao={inspecao} />}
        </div>
        <p className="font-mono text-xs text-slate-500">{inspecao.codigo}</p>
        <h2 className="text-lg font-bold text-slate-900">{checklist?.nome ?? 'Inspeção'}</h2>
        {erroGravacao ? (
          <p className="mt-1 rounded bg-alta/10 px-2 py-1 text-xs text-alta" role="alert">
            {erroGravacao}
          </p>
        ) : (
          salvandoEm && (
            <p className="mt-0.5 text-xs text-leve">
              Salvo no aparelho às {new Date(salvandoEm).toLocaleTimeString('pt-BR')}
            </p>
          )
        )}
      </header>

      {semFormulario && (
        <div className="mb-4 rounded-lg border border-moderada/30 bg-moderada/5 p-3 text-sm text-moderada" role="status">
          O checklist desta inspeção foi desativado por um administrador e saiu do aparelho. As respostas continuam
          guardadas; a inspeção volta a ser editável quando o checklist for reativado.
        </div>
      )}

      {inspecao.problema && (
        <div className="mb-4 rounded-lg border border-moderada/30 bg-moderada/5 p-3 text-sm text-moderada">
          {inspecao.problema}
        </div>
      )}

      {problemas.length > 0 && (
        <div className="mb-4 rounded-lg border border-alta/30 bg-alta/5 p-3" role="alert">
          <p className="mb-1 font-semibold text-alta">
            {problemas.length === 1 ? 'Falta 1 campo' : `Faltam ${problemas.length} campos`}
          </p>
          <ul className="list-inside list-disc text-sm text-alta">
            {problemas.map((p) => (
              <li key={p.campoId}>
                {p.campoNome}: {p.mensagem}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {secoes.map(({ secao, campos: doGrupo }) => (
          <div key={secao || '_'}>
            {secao && (
              <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {secao}
              </h3>
            )}
            {/* Duas colunas no computador; texto longo, foto e assinatura ocupam a
                linha inteira, que é o espaço que eles pedem. */}
            <div className="grid gap-3 lg:grid-cols-2">
              {doGrupo.map((campo) => (
                <div key={campo.id} className={ocupaLinhaInteira(campo) ? 'lg:col-span-2' : ''}>
                  <CampoResposta
                    campo={campo}
                    valor={valores.get(campo.id)}
                    inspecaoId={inspecao.id}
                    somenteLeitura={somenteLeitura}
                    lista={listaPorCampo.get(campo.id)}
                    responsaveis={responsaveisDaObra}
                    onMudar={mudarCampo}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        <GaleriaFotos
          inspecaoId={inspecao.id}
          fotos={inspecao.fotos}
          somenteLeitura={somenteLeitura}
          onMudar={mudarFotos}
        />

      </div>

      {!somenteLeitura && (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="largura flex gap-2 lg:justify-end">
            <button onClick={() => void salvarRascunho()} className="botao-neutro flex-1 lg:w-56 lg:flex-none">
              Salvar rascunho
            </button>
            <button
              onClick={() => void finalizar()}
              disabled={semFormulario}
              className="botao-primario flex-1 lg:w-56 lg:flex-none"
            >
              Finalizar
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

type OpcaoResponsavel = { id: string; nome: string }

/// Campos que ficam apertados em meia largura: texto longo (descrição, plano de
/// ação), foto, assinatura e escolha múltipla com muitas opções.
function ocupaLinhaInteira(campo: CampoChecklist): boolean {
  if (campo.tipoCampo === 'IMAGEM' || campo.tipoCampo === 'ASSINATURA') return true
  if (campo.tipoCampo === 'MULTISSELECAO') return campo.opcoes.length > 6
  if (campo.tipoCampo !== 'TEXTO' || campo.opcoes.length > 0) return false
  const nome = campo.nome.toLowerCase()
  return nome.includes('descri') || nome.includes('plano')
}

/// O INI tem campo "Titulo"; os checklists dinâmicos não. Sem título, a lista da
/// home fica só com códigos, então usa o primeiro texto preenchido.
function tituloDeduzido(respostas: Resposta[]): string | null {
  const titulo = respostas.find((r) => r.campoNome.toLowerCase() === 'titulo')
  if (titulo?.texto) return titulo.texto
  const primeiroTexto = respostas.find((r) => r.campoTipo === 'TEXTO' && (r.texto || r.textoLongo))
  const valor = primeiroTexto?.texto ?? primeiroTexto?.textoLongo
  return valor ? valor.slice(0, 120) : null
}
